import { useEffect } from "react"
import type { ReactNode } from "react"
import { Navigate, useLocation } from "react-router"
import { redirectToSso } from "@alascorporation/api-client"
import { hasCurrentPolicy, isStaffAuthenticated, mustChangePassword, type PermissionKey } from "../../lib/auth"
import { ssoConfig } from "../../lib/sso"

type ProtectedRouteProps = {
  children: ReactNode
  /** Policy key(s) the current profile must have. Omit for routes any authenticated staff profile can reach. */
  requires?: PermissionKey | PermissionKey[]
}

export default function ProtectedRoute({ children, requires }: ProtectedRouteProps) {
  const location = useLocation()
  const authenticated = isStaffAuthenticated()

  useEffect(() => {
    if (!authenticated) {
      redirectToSso(ssoConfig, location.pathname + location.search)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, location.pathname, location.search])

  if (!authenticated) {
    return null
  }

  // A forced password change blocks every other screen except the one that lets you set it.
  if (mustChangePassword() && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />
  }

  if (requires) {
    const keys = Array.isArray(requires) ? requires : [requires]
    if (!hasCurrentPolicy(...keys)) {
      return <Navigate to="/" replace />
    }
  }

  return <>{children}</>
}
